import { useState, useEffect, useRef, useMemo } from "react";
import MDEditor, { commands } from "@uiw/react-md-editor";
import config from "../config";

const SOP_API = config.api.sopApi;

const defaultP2PContent = `# P2P Exception Handling SOP

## Overview
This document outlines the Standard Operating Procedure for handling Procure-to-Pay exceptions.

## Exception Types

### 1. Currency Mismatch
- **Definition:** Invoice currency differs from PO currency
- **Action:** Verify exchange rate, contact vendor if discrepancy exceeds threshold
- **Resolution:** Apply correct exchange rate or request corrected invoice

### 2. Late Invoicing
- **Definition:** Invoice received after payment deadline
- **Action:** Check payment terms, verify delivery confirmation
- **Resolution:** Process with late fee waiver or escalate to procurement

### 3. Price Variance
- **Definition:** Invoice amount differs from PO amount
- **Action:** Compare line items, check for partial deliveries
- **Resolution:** Accept if within tolerance, reject and request credit note if exceeds

### 4. Duplicate Invoice
- **Definition:** Same invoice submitted multiple times
- **Action:** Verify against payment history
- **Resolution:** Block duplicate, confirm original payment status

## Escalation Matrix

| Level | Condition | Action |
|-------|-----------|--------|
| L1 | Variance < 1% | Auto-resolve |
| L2 | Variance 1-5% | Agent review |
| L3 | Variance > 5% | Manager approval |

## Notes
- All exceptions must be resolved within **48 hours**
- Document all actions in the audit trail
- Notify vendor within **24 hours** of exception detection
`;

const defaultM2CContent = `# Meter-to-Cash Exception Handling SOP

## Overview
This document outlines the Standard Operating Procedure for handling Meter-to-Cash (M2C) billing exceptions.

## Exception Types

### 1. Zero Consumption
- **Definition:** Meter reports no usage for a billing period that historically shows consumption
- **Action:** Verify meter connectivity, check for tampering or outages
- **Resolution:** Estimate based on history or dispatch field inspection

### 2. Negative Consumption
- **Definition:** Current read is lower than the previous read
- **Action:** Validate read accuracy, check for meter rollover or replacement
- **Resolution:** Correct the read or raise an adjustment

### 3. Billing Estimate Override
- **Definition:** Estimated bill differs significantly from actual read
- **Action:** Compare estimate against actual, review estimation logic
- **Resolution:** Rebill with actual read and notify customer

### 4. Unbilled Account
- **Definition:** Active meter with no invoice generated for the period
- **Action:** Verify account status and rate plan assignment
- **Resolution:** Trigger billing run and confirm invoice issuance

## Escalation Matrix

| Level | Condition | Action |
|-------|-----------|--------|
| L1 | Variance < 5% | Auto-resolve |
| L2 | Variance 5-15% | Agent review |
| L3 | Variance > 15% | Supervisor approval |

## Notes
- All exceptions must be resolved within **48 hours**
- Document all actions in the audit trail
- Notify the customer of any rebilling within **24 hours**
`;

const defaultOrderProcessingContent = `# Order Processing Exception Management Standard

## O2C-OPS v0.1

## Overview
This document outlines the Standard Operating Procedure for Order Processing, including credit clearance and item master validation.

---

## Scenario Dispatch — Credit

### Credit Decision Scenarios

| Scenario | Condition | Decision | Tier |
|----------|-----------|----------|------|
| CR_WITHIN_LIMIT | Exposure ≤ 100% of limit, no adverse signal | AUTO_RELEASE | — |
| CR_TOLERANCE_BAND | Exposure 100–110%, clean aging, days-to-pay stable | AUTO_RELEASE | — |
| CR_OVER_LIMIT_CLEAN | Exposure 110–150%, no aging deterioration | ESCALATE | T2 |
| **CR_AGING_DETERIORATION** | Any balance in 61–90 bucket, or days-to-pay worsened > 25% over 12 months | ESCALATE | T2 |
| CR_SEVERE_EXPOSURE | Exposure 150–200% | ESCALATE | T3 |
| CR_CRITICAL_EXPOSURE | Exposure > 200%, or order > $100,000 | ESCALATE | T4 |
| CR_NO_HISTORY | No credit limit on file, or account under 90 days old | CREDIT_APPLICATION | — |
| CR_BLOCKING | Any 90+ balance, active collections, returned payment in 90 days | HOLD | T3 to override |

### Partial Release Rule
Where a scenario resolves to ESCALATE and no blocking signal is present, credit_check must compute a partial release that brings exposure back to 100% of limit, and name the specific invoice whose payment would clear the balance.

---

## Tolerance Bands

Credit exposure tolerance by customer tenure and payment record:

| Tenure | Payment Record | Tolerance |
|--------|----------------|-----------|
| < 12 months | Any | 0% |
| 1–3 years | Clean, stable | 5% |
| 3+ years | Clean, stable | 10% |
| **3+ years** | Any 61+ balance in last 6 months | 0% |
| Any | Returned payment in last 90 days | 0% |

---

## Scenario Dispatch — Item Master

### Item Resolution Scenarios

| Scenario | Condition | Action |
|----------|-----------|--------|
| IM_MATCHED | Resolved, confidence ≥ 0.90, no substitution | Accept line |
| **IM_SUPERSEDED** | Part discontinued, direct replacement exists | Substitute, record substitution_from, state what changed |
| IM_MULTI_CANDIDATE | Two or more candidates above 0.75 | NEEDS_REVIEW, list candidates |
| IM_LOW_CONFIDENCE | Extraction confidence below 0.60 | NEEDS_REVIEW, cite unreadable field |
| IM_DISCONTINUED_NO_SUB | Discontinued, no replacement on file | NEEDS_REVIEW |
| IM_PRICE_VARIANCE | Quoted price differs from contract list by > 5% | Flag, continue, do not override |
| IM_NO_STOCK | Ship-from branch has no stock | Suggest alternate branch |

### Resolution Routing
Two failure modes, kept distinct:
- **IM_LOW_CONFIDENCE** means the character could not be read — a human looks at the source document.
- **IM_MULTI_CANDIDATE** and **IM_DISCONTINUED_NO_SUB** mean the read was clean but does not resolve — a human makes a product decision.

These route to different people for efficient resolution.

---

## Notes
- Agents fetch this SOP at run time and record the branch matched against every decision
- Agents execute in Karini and post to Epicor Prophet 21
- All escalations must be resolved within SLA timeframes (T2: 8h, T3: 4h, T4: 2h)
- Document all decisions in the audit trail
`;

const defaultCreditManagementContent = `# Credit Management Exception Standard

## CM-OPS v1.0

## Overview
This document outlines the Standard Operating Procedure for Credit Management within the Order-to-Cash process. It covers credit risk assessment, limit checks, approval workflows, and escalation procedures.

---

## Credit Check Framework

### Check Types

| Check ID | Check Name | Logic | Threshold |
|----------|-----------|-------|-----------|
| A | Static Credit Limit Check | Total exposure vs. credit limit (with tolerance) | 100% of limit (+ tenure tolerance) |
| A2 | Dynamic Credit Limit Check | Exposure vs. estimated 90-day revenue capacity | Avg monthly revenue × 3 |
| B | Overdue Receivables Check | Total overdue as % of limit + aging buckets | No overdue OR < 10% of limit |
| C | Maximum Document Value Check | Single order value cap | $100,000 default |
| D | Payment Terms Deviation Check | Order terms vs. customer master terms | Must match |
| E | Oldest Open Item Check | Days outstanding of oldest unpaid invoice | 90 days (medium risk), 60 days (high risk) |
| E2 | Maximum Dunning Level Check | Current dunning level | Level < 3 |

---

## Scenario Dispatch — Credit Decision

| Scenario | Condition | Decision | Approval Tier |
|----------|-----------|----------|---------------|
| CM_WITHIN_LIMIT | Exposure ≤ 100% of limit, no adverse signal | AUTO_RELEASE | — |
| CM_TOLERANCE_BAND | Exposure 100–110%, clean aging, days-to-pay stable | AUTO_RELEASE | — |
| CM_OVER_LIMIT_CLEAN | Exposure 110–150%, no aging deterioration | ESCALATE | T2 (Credit Manager) |
| CM_AGING_DETERIORATION | Any balance in 61–90 bucket, or days-to-pay worsened > 25% | ESCALATE | T2 |
| CM_SEVERE_EXPOSURE | Exposure 150–200%, or 3+ blocking checks | HOLD | T3 (Controller) |
| CM_CRITICAL_EXPOSURE | Exposure > 200%, or order > $100,000 | HOLD | T4 (CFO) |
| CM_NO_HISTORY | No credit limit on file, or account under 90 days old | CREDIT_APPLICATION | — |
| CM_BLOCKING | Any 90+ balance, active collections, returned payment in 90 days | HOLD | T3 to override |

---

## Tolerance Bands

Credit exposure tolerance by customer tenure and payment record:

| Tenure | Payment Record | Tolerance |
|--------|----------------|-----------|
| < 12 months | Any | 0% |
| 1–3 years | Clean, stable | 5% |
| 3+ years | Clean, stable | 10% |
| 3+ years | Any 61+ balance in last 6 months | 0% |
| Any | Returned payment in last 90 days | 0% |

---

## Approval Tiers & SLA

| Tier | Role | SLA | Triggered When |
|------|------|-----|---------------|
| T1 | Supervisor | 24h | Manual override of auto-release |
| T2 | Credit Manager | 8h | Exposure 110–150%, aging deterioration |
| T3 | Controller | 4h | Exposure > 150%, 3+ blocking checks, HOLD decisions |
| T4 | CFO | 2h | Exposure > 200%, orders > $100K |

---

## Release Criteria

An order on HOLD may be released when ANY of the following are satisfied:

1. Payment received to reduce exposure below credit limit
2. Temporary tolerance approved by credit manager (T2+)
3. Credit limit increased by authorized approver
4. Additional security provided (guarantee, prepayment)
5. Partial release computed to bring exposure to 100% of limit

### Partial Release Rule
Where a scenario resolves to ESCALATE or HOLD and no absolute blocking signal (90+ aging, collections, returned payment) is present:
- Compute partial release amount that brings exposure back to 100% of limit
- Name the specific clearing invoice whose payment would release the full order
- Stage the remainder for release upon payment

---

## Notifications

| Event | Channel | Recipients |
|-------|---------|-----------|
| AUTO_RELEASE | None | — |
| ESCALATE | Email | Credit Manager, Order Desk |
| HOLD | Email + Queue | Credit Manager, Controller, Order Desk |
| CREDIT_APPLICATION | Email | Credit Analyst |

### Subject Format
- \`[CM-RELEASED]\` — Auto-released orders
- \`[CM-ESCALATED]\` — Orders requiring credit review
- \`[CM-BLOCKED]\` — Orders on hold pending approval

---

## Agent Workflow

### Step 1: Order Extraction Agent
- Parse source document (PDF/email)
- Match customer ID and validate account
- Extract line items, quantities, prices
- Determine payment terms

### Step 2: Item Master Validation Agent
- Resolve each line item against item master
- Handle superseded parts (auto-substitute)
- Flag multi-candidate or low-confidence matches
- Calculate order total

### Step 3: Credit Check Agent
- Execute all credit checks (A through E2)
- Apply tolerance bands based on tenure
- Determine scenario code and decision
- Compute partial release if applicable
- Generate recommendation and notifications

---

## Notes
- All credit decisions are recorded in \`credit_workflow_state.agent3_output\`
- Agents execute in Karini and post results to ERP system
- Payment trend analysis uses 12-month rolling window
- Aging buckets: Current, 1–30, 31–60, 61–90, 90+
- All blocking checks must be resolved before full release
`;

// Each process points at its own S3 document. Add new entries here to support
// more processes without duplicating the editor.
const DOC_CONFIG = {
  p2p: {
    key: `${config.s3.sopPath}/${config.sopKeys.p2p}`,
    title: "P2P Exception Handling Documentation",
    defaultContent: defaultP2PContent,
  },
  m2c: {
    key: `${config.s3.sopPath}/${config.sopKeys.m2c}`,
    title: "Meter-to-Cash Exception Handling Documentation",
    defaultContent: defaultM2CContent,
  },
  "order-processing": {
    key: `${config.s3.sopPath}/${config.sopKeys.orderProcessing}`,
    title: "Order Processing Exception Management Documentation",
    defaultContent: defaultOrderProcessingContent,
  },
  "credit-management": {
    key: `${config.s3.sopPath}/${config.sopKeys.creditManagement}`,
    title: "Credit Management SOP Documentation",
    defaultContent: defaultCreditManagementContent,
  },
  "cash-application": {
    key: `${config.s3.sopPath}/${config.sopKeys.cashApplication}`,
    title: "Cash Application Exception Handling Documentation",
    defaultContent: defaultCreditManagementContent, // Will use same default for now
  },
};

function getDocConfig() {
  const params = new URLSearchParams(window.location.search);
  const doc = (params.get("doc") || "p2p").toLowerCase();
  return DOC_CONFIG[doc] || DOC_CONFIG.p2p;
}

function MarkdownEditorPage() {
  const docConfig = useMemo(() => getDocConfig(), []);
  const [value, setValue] = useState(docConfig.defaultContent);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("");
  const [previewMode, setPreviewMode] = useState("live");
  const [isFullscreen, setIsFullscreen] = useState(false);

  const saveToS3Ref = useRef(null);
  const editorWrapRef = useRef(null);
  const gutterRef = useRef(null);

  const lineCount = useMemo(
    () => (value ? value.split("\n").length : 1),
    [value]
  );

  // Keep the line-number gutter aligned and scroll-synced with the editor's text area.
  useEffect(() => {
    const wrap = editorWrapRef.current;
    const gutter = gutterRef.current;
    if (!wrap || !gutter) return;
    const area = wrap.querySelector(".w-md-editor-area");
    const toolbar = wrap.querySelector(".w-md-editor-toolbar");
    if (!area) return;

    const align = () => {
      // Push the gutter below the toolbar so line 1 lines up with the first line.
      gutter.style.marginTop = (toolbar ? toolbar.offsetHeight : 0) + "px";
      gutter.style.height = area.clientHeight + "px";
    };
    const onScroll = () => {
      gutter.scrollTop = area.scrollTop;
    };

    align();
    onScroll();
    // Re-align on the next frame in case the toolbar mounts after this effect.
    const raf = requestAnimationFrame(align);
    area.addEventListener("scroll", onScroll);
    window.addEventListener("resize", align);
    return () => {
      cancelAnimationFrame(raf);
      area.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", align);
    };
  }, [loading, previewMode, isFullscreen, lineCount]);

  // Listen for fullscreen changes (e.g. Escape key)
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  useEffect(() => {
    const loadFromS3 = async () => {
      setLoading(true);
      try {
        const response = await fetch(SOP_API, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "read", key: docConfig.key }),
        });
        if (response.ok) {
          const data = await response.json();
          if (data.content && data.content.trim().length > 0) {
            setValue(data.content);
          }
        }
      } catch (err) {
        console.error("Failed to load SOP from S3:", err);
      } finally {
        setLoading(false);
      }
    };
    loadFromS3();
  }, [docConfig]);

  const saveToS3 = async () => {
    setSaving(true);
    setSaveStatus("");
    try {
      const response = await fetch(SOP_API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "write", key: docConfig.key, content: value }),
      });
      if (response.ok) {
        setSaveStatus("saved");
      } else {
        setSaveStatus("error");
      }
    } catch (err) {
      console.error("Failed to save SOP to S3:", err);
      setSaveStatus("error");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    saveToS3Ref.current = saveToS3;
  });

  const saveCommand = useMemo(
    () => ({
      name: "save",
      keyCommand: "save",
      buttonProps: { "aria-label": "Save to S3", title: "Save to S3" },
      icon: (
        <span className="sop-save-icon">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
            <polyline points="17 21 17 13 7 13 7 21"/>
            <polyline points="7 3 7 8 15 8"/>
          </svg>
          {saving ? "Saving…" : "Save"}
        </span>
      ),
      execute: () => { saveToS3Ref.current?.(); },
    }),
    [saving]
  );

  return (
    <div className="min-h-screen" style={{ background: isFullscreen ? "#ffffff" : "linear-gradient(135deg, #f0f4ff 0%, #e8eeff 50%, #f5f7ff 100%)" }} data-color-mode="light">
      {/* Blue Header */}
      {!isFullscreen && (
      <div style={{ background: "#0040B0" }} className="px-6 py-4 shadow-lg">
        <div style={{ maxWidth: "95vw", margin: "0 auto" }}>
          <h1 className="text-lg font-bold text-white">SOP Editor</h1>
          <p className="text-sm text-blue-200">{docConfig.title}</p>
        </div>
      </div>
      )}

      {/* Custom Toolbar Row */}
      <div style={{ maxWidth: isFullscreen ? "100%" : "95vw", margin: "0 auto" }} className={isFullscreen ? "" : "px-4"}>
        <div className={`bg-white border border-gray-200 border-b-0 px-5 py-3 flex items-center justify-between ${isFullscreen ? "" : "rounded-t-xl mt-6"}`}>
          {/* Left: Mode tabs */}
          <div className="flex items-center gap-0">
            <button
              onClick={() => setPreviewMode("edit")}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${previewMode === "edit" ? "border-[#0040B0] text-[#0040B0]" : "border-transparent text-gray-600 hover:text-gray-900"}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M7 7h10M7 12h10M7 17h6"/></svg>
              Markdown
            </button>
            <button
              onClick={() => setPreviewMode("preview")}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${previewMode === "preview" && previewMode !== "live" ? "border-[#0040B0] text-[#0040B0]" : "border-transparent text-gray-600 hover:text-gray-900"}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
              Preview
            </button>
          </div>

          {/* Right: View buttons + Save */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPreviewMode("live")}
              className={`flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold rounded-md border transition-colors ${previewMode === "live" ? "border-[#0040B0] text-[#0040B0] bg-blue-50" : "border-gray-300 text-gray-700 hover:border-[#0040B0] hover:text-[#0040B0]"}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="3" x2="12" y2="21"/></svg>
              Split View
            </button>
            <button
              onClick={() => {
                if (!document.fullscreenElement) {
                  document.documentElement.requestFullscreen();
                  setIsFullscreen(true);
                } else {
                  document.exitFullscreen();
                  setIsFullscreen(false);
                }
              }}
              className={`flex items-center gap-2 px-3.5 py-1.5 text-xs font-semibold rounded-md border transition-colors ${isFullscreen ? "border-[#0040B0] text-[#0040B0] bg-blue-50" : "border-gray-300 text-gray-700 hover:border-[#0040B0] hover:text-[#0040B0]"}`}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
              {isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            </button>
            
            <button
              onClick={saveToS3}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-1.5 text-xs font-semibold rounded-md bg-[#0040B0] text-white hover:bg-[#003590] transition-colors shadow-sm disabled:opacity-50"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"/>
                <polyline points="17 21 17 13 7 13 7 21"/>
                <polyline points="7 3 7 8 15 8"/>
              </svg>
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>

      {/* Editor */}
      <div className={isFullscreen ? "" : "mx-auto px-4"} style={{ maxWidth: isFullscreen ? "100%" : "95vw" }}>
        {loading ? (
          <div className={`flex items-center justify-center py-20 bg-white border border-gray-200 border-t-0 ${isFullscreen ? "" : "rounded-b-xl"}`}>
            <div className="inline-block w-8 h-8 border-4 border-blue-200 border-t-[#0040B0] rounded-full animate-spin"></div>
          </div>
        ) : (
          <div className={`overflow-hidden border border-gray-200 border-t-0 ${isFullscreen ? "" : "rounded-b-xl shadow-xl"}`}>
            <div ref={editorWrapRef} className="sop-editor-wrap" style={{ display: "flex", alignItems: "flex-start" }}>
              <div
                ref={gutterRef}
                className="sop-line-gutter"
                aria-hidden="true"
              >
                {Array.from({ length: lineCount }, (_, i) => (
                  <div key={i} className="sop-line-no">{i + 1}</div>
                ))}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <MDEditor
                  value={value}
                  onChange={setValue}
                  height={isFullscreen ? "calc(100vh - 95px)" : 780}
                  preview={previewMode}
                  visibleDragbar={false}
                  style={{ fontSize: "15px" }}
                  extraCommands={[]}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Save status popup */}
      {saveStatus && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4 text-center">
            {saveStatus === "saved" ? (
              <>
                <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Saved Successfully</h3>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="15" y1="9" x2="9" y2="15"/>
                    <line x1="9" y1="9" x2="15" y2="15"/>
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-1">Save Failed</h3>
                <p className="text-sm text-gray-500 mb-5">Unable to save the document. Please try again.</p>
              </>
            )}
            <button
              type="button"
              onClick={() => setSaveStatus("")}
              className="inline-flex items-center justify-center px-6 py-2 text-sm font-semibold rounded-lg bg-[#0040B0] text-white hover:bg-[#003590] transition-colors shadow-sm"
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* Style overrides */}
      <style>{`
        .w-md-editor-toolbar {
          padding: 6px 12px !important;
          min-height: 38px !important;
          border-top: 1px solid #e5e7eb !important;
          background: #fafbfc !important;
        }
        .w-md-editor-toolbar li > button {
          width: 30px !important;
          height: 30px !important;
          color: #1f2937 !important;
        }
        .w-md-editor-toolbar li > button svg {
          width: 16px !important;
          height: 16px !important;
          color: #1f2937 !important;
          fill: currentColor !important;
        }
        .w-md-editor-toolbar li > button:hover {
          background: #e5e7eb !important;
        }
        .w-md-editor-text-pre,
        .w-md-editor-text-input,
        .w-md-editor-text {
          font-size: 15px !important;
          line-height: 24px !important;
          color: #1f2937 !important;
        }
        .w-md-editor-text-pre > code,
        .w-md-editor-text-pre > code * {
          font-size: 15px !important;
          line-height: 24px !important;
        }
        .wmde-markdown {
          font-size: 15px !important;
          line-height: 1.7 !important;
          color: #1f2937 !important;
        }
        /* Line-number gutter, kept in sync with the editor via JS scroll */
        .sop-line-gutter {
          flex: 0 0 auto;
          width: 48px;
          padding: 10px 8px 10px 0;
          margin: 0;
          box-sizing: border-box;
          overflow: hidden;
          text-align: right;
          background: #fafbfc;
          border-right: 1px solid #e5e7eb;
          font-family: var(--md-editor-font-family, ui-monospace, SFMono-Regular, Consolas, monospace);
          font-size: 13px;
          color: #9ca3af;
          user-select: none;
        }
        .sop-line-no {
          height: 24px;
          line-height: 24px;
        }
        /* Disable soft-wrap so 1 logical line === 1 visual row of 24px,
           keeping the gutter numbers perfectly aligned. Long lines scroll
           horizontally instead of wrapping. */
        .sop-editor-wrap .w-md-editor-text,
        .sop-editor-wrap .w-md-editor-text-pre,
        .sop-editor-wrap .w-md-editor-text-input,
        .sop-editor-wrap .w-md-editor-text-pre > code {
          white-space: pre !important;
          word-break: normal !important;
          overflow-wrap: normal !important;
        }
        .sop-editor-wrap .w-md-editor-area {
          overflow: auto !important;
        }
      `}</style>
    </div>
  );
}

export default MarkdownEditorPage;
